/**
 * Keyboard camera control, active only while free look is on.
 *
 *   W / S        PAN    slide the view up / down its own plane
 *   A / D        PAN    slide the view left / right
 *   space/shift  LIFT   up / down along WORLD up
 *   ← / →        ROLL   bank the horizon
 *   ↑ / ↓        PITCH  tilt the view up / down
 *   trackpad            zoom (OrbitControls owns this)
 *
 * Three kinds of motion, deliberately separated: pan slides the rig
 * without changing where it points, lift moves along gravity regardless
 * of where you are looking, and roll/pitch rotate without moving. There
 * is no forward/back key — pinch-zoom covers it.
 *
 * ORBITCONTROLS HAS NO ROLL
 * -------------------------
 * It maintains the camera as `target + spherical offset` and finishes
 * every update with lookAt(target), which reconstructs the orientation
 * from `camera.up`. Rotating the camera directly is therefore pointless:
 * the next frame overwrites it.
 *
 * So roll rotates `camera.up` about the view axis instead. OrbitControls
 * builds its spherical frame FROM that vector — it is designed to support
 * Z-up scenes — so banking the up vector banks the whole view, and does
 * it in a way that survives update() rather than fighting it.
 *
 * `up` is projected onto the plane perpendicular to the view direction
 * before rolling. Without that step it traces a cone rather than a
 * circle, and if it ever drifted near-parallel to the view axis, lookAt
 * would have no defined orientation.
 *
 * PITCH ORBITS RATHER THAN TURNING IN PLACE
 * -----------------------------------------
 * Turning the camera in place would be undone by lookAt for the same
 * reason. Pitch therefore rotates the camera POSITION around the pivot,
 * which OrbitControls then re-derives cleanly. It is the keyboard
 * equivalent of a vertical drag, not a free-flight pitch.
 *
 * MOVING BOTH THE CAMERA AND THE PIVOT
 * ------------------------------------
 * For the translating motions, the camera and `controls.target` move by
 * the same vector. Translating only `camera.position` does nothing — the
 * next update() snaps it back to `target + offset`. Moving both slides
 * the rig and leaves the pivot in front of you, so a later drag still
 * orbits what you are looking at.
 *
 * SPEED SCALES WITH DISTANCE
 * --------------------------
 * A fixed metres-per-second is violent up close and glacial with the
 * whole map in frame. Scaling translation by the distance to the pivot
 * makes one keypress cover a consistent FRACTION of what you can
 * currently see, which is what reads as constant speed. Rotation is a
 * fixed angular rate, because an angle is already scale-free.
 */
import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import { useAppStore } from '../state/store';

/** Fraction of the current view distance covered per second at full tilt. */
const SPEED = 0.9;
/** Floor so movement never stalls when parked on top of a node. */
const MIN_SPEED = 2.5;
/** Radians per second for roll and pitch. */
const ROLL_RATE = 1.1;
const PITCH_RATE = 0.9;
/** Seconds to reach full speed. Takes the twitch off a single tap. */
const RAMP = 0.18;

/**
 * How far the pitch may drive the camera toward the poles.
 *
 * At exactly overhead the offset becomes parallel to `up` and the
 * spherical frame has no defined azimuth — the view spins. OrbitControls
 * keeps its own EPS away from the poles for the same reason; this keeps
 * the keyboard from walking into them.
 */
const POLE_LIMIT = 0.08;

/** Keys we consume. Anything else is left to the page. */
const KEYS = new Set([
  'KeyW', 'KeyA', 'KeyS', 'KeyD',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'Space', 'ShiftLeft', 'ShiftRight',
]);

/**
 * When the keyboard belongs to something else.
 *
 * A global keydown listener on a page with real UI collides constantly,
 * and every collision reads to the user as "typing broke the app":
 *
 *   fields        arrows must move the caret, space must type a space,
 *                 and WASD must type letters. contentEditable counts —
 *                 the feedback form uses it.
 *   buttons/links a focused control activates on space. Stealing it there
 *                 breaks the control AND moves the camera at once.
 *                 LineCarousel does exactly this.
 *   dialogs       a modal owns the keyboard while it is up.
 */
function keyboardBelongsElsewhere(): boolean {
  if (document.querySelector('[role="dialog"], dialog[open]')) return true;
  const el = document.activeElement as HTMLElement | null;
  if (!el || el === document.body) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  if (tag === 'BUTTON' || tag === 'A' || el.getAttribute('role') === 'button') return true;
  return false;
}

export function FreeLookKeys({ controls }: { controls: React.RefObject<OrbitControlsImpl> }) {
  const freeLook = useAppStore((s) => s.freeLook);
  const camera = useThree((s) => s.camera);
  const held = useRef<Set<string>>(new Set());
  const ramp = useRef(0);

  // Scratch, allocated once — this runs every frame.
  const forward = useRef(new THREE.Vector3());
  const right = useRef(new THREE.Vector3());
  const screenUp = useRef(new THREE.Vector3());
  const step = useRef(new THREE.Vector3());
  const offset = useRef(new THREE.Vector3());
  const WORLD_UP = useRef(new THREE.Vector3(0, 1, 0));

  useEffect(() => {
    if (!freeLook) {
      // Leaving the mode with a key down would strand it held and leave
      // the camera drifting, since the keyup is never seen.
      held.current.clear();
      ramp.current = 0;
      return;
    }
    const down = (e: KeyboardEvent) => {
      if (keyboardBelongsElsewhere() || !KEYS.has(e.code)) return;
      // Modifier chords belong to whoever bound them — the drawer uses
      // Cmd/Alt+Arrow for back/forward through selection history, and
      // unqualified this would navigate AND move the camera while
      // preventDefault quietly broke the navigation. Shift is exempt: it
      // is our own "down" binding, and shift+W is a legitimate combo.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Arrows scroll the page, space scrolls a whole screen.
      e.preventDefault();
      held.current.add(e.code);
    };
    const up = (e: KeyboardEvent) => held.current.delete(e.code);
    // A tab-away swallows the keyup, leaving a key stuck down.
    const clear = () => held.current.clear();

    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', clear);
      held.current.clear();
    };
  }, [freeLook]);

  useFrame((_, delta) => {
    const c = controls.current;
    if (!freeLook || !c) return;

    const keys = held.current;
    const panX = (keys.has('KeyD') ? 1 : 0) - (keys.has('KeyA') ? 1 : 0);
    const panY = (keys.has('KeyW') ? 1 : 0) - (keys.has('KeyS') ? 1 : 0);
    const lift =
      (keys.has('Space') ? 1 : 0) -
      (keys.has('ShiftLeft') || keys.has('ShiftRight') ? 1 : 0);
    const roll = (keys.has('ArrowRight') ? 1 : 0) - (keys.has('ArrowLeft') ? 1 : 0);
    const pitch = (keys.has('ArrowUp') ? 1 : 0) - (keys.has('ArrowDown') ? 1 : 0);

    const idle = !panX && !panY && !lift && !roll && !pitch;
    if (idle) {
      // Decay rather than cut, so releasing coasts to a stop.
      ramp.current = Math.max(0, ramp.current - delta / RAMP);
      if (ramp.current === 0) return;
    } else {
      ramp.current = Math.min(1, ramp.current + delta / RAMP);
    }

    // Clamped: a long frame (restored tab, GC pause) would otherwise
    // teleport the camera across the map in one step.
    const dt = Math.min(delta, 0.05);
    const r = ramp.current;

    camera.getWorldDirection(forward.current);

    /* --- axes ---------------------------------------------------- */
    // `right` is the screen-plane right. Taken from the camera's own
    // matrix rather than forward x worldUp, because once roll is in play
    // the two diverge — and the cross-product form collapses to a zero
    // vector (then NaN, then a vanished camera) when looking straight up
    // or down.
    right.current.setFromMatrixColumn(camera.matrixWorld, 0).normalize();
    screenUp.current.setFromMatrixColumn(camera.matrixWorld, 1).normalize();

    /* --- translation: pan and lift -------------------------------- */
    const dist = camera.position.distanceTo(c.target);
    const speed = Math.max(MIN_SPEED, dist * SPEED) * r * dt;

    step.current.set(0, 0, 0);
    if (panX) step.current.addScaledVector(right.current, panX);
    if (panY) step.current.addScaledVector(screenUp.current, panY);
    // World up, not screen up: space should climb even when pitched down.
    if (lift) step.current.addScaledVector(WORLD_UP.current, lift);

    if (step.current.lengthSq() > 0) {
      // Normalise so a diagonal is not faster than a straight line.
      step.current.normalize().multiplyScalar(speed);
      // Both, together — otherwise update() snaps the camera back.
      camera.position.add(step.current);
      c.target.add(step.current);
    }

    /* --- roll: rotate the UP vector ------------------------------- */
    if (roll) {
      // Orthogonalise before rotating. `camera.up` is not generally
      // perpendicular to the view direction, and rotating a
      // non-perpendicular vector about that axis sweeps a cone rather
      // than banking — the horizon would wobble instead of turning.
      screenUp.current.copy(camera.up).projectOnPlane(forward.current);
      if (screenUp.current.lengthSq() > 1e-8) {
        screenUp.current.normalize().applyAxisAngle(forward.current, -roll * ROLL_RATE * r * dt);
        camera.up.copy(screenUp.current);
      }
    }

    /* --- pitch: orbit the position around the pivot --------------- */
    if (pitch) {
      offset.current.copy(camera.position).sub(c.target);
      offset.current.applyAxisAngle(right.current, pitch * PITCH_RATE * r * dt);
      // MEASURE, DO NOT PREDICT.
      //
      // The obvious clamp is arithmetic: angle-to-up minus the rotation
      // equals the angle after. That silently assumes rotating positively
      // about `right` DECREASES the angle to up, which depends on the
      // handedness of `right` against the current offset — and testing it
      // showed the assumption backwards, so the clamp would have pushed
      // the camera INTO the pole it was meant to protect.
      //
      // Rotating a scratch vector and measuring the result needs no such
      // assumption. Per-frame steps are small (~0.05 rad), so refusing an
      // over-limit step stops the camera within one step of the pole
      // rather than letting the azimuth become undefined and spin.
      const after = offset.current.angleTo(camera.up);
      if (after > POLE_LIMIT && after < Math.PI - POLE_LIMIT) {
        camera.position.copy(c.target).add(offset.current);
      }
    }
  });

  return null;
}
